import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, TrendingUp, Clock, Users, CheckCircle, ArrowRight } from 'lucide-react';

function CourseCard({ course }) {
  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    // Full stars
    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <Star key={`full-${i}`} className="w-4 h-4 fill-current text-yellow-500" />
      );
    }

    // Half star
    if (hasHalfStar) {
      stars.push(
        <Star key="half" className="w-4 h-4 fill-current text-yellow-500" />
      );
    }

    // Empty stars
    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <Star key={`empty-${i}`} className="w-4 h-4 text-slate-300" />
      );
    }

    return stars;
  };

  return (
    <motion.div
      whileHover={{ y: -4 }}
      transition={{ duration: 0.3 }}
      className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-slate-100 h-full flex flex-col"
    >
      {/* Course Image */}
      <div className="relative overflow-hidden">
        <div className="w-full h-48 bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
          <TrendingUp className="w-16 h-16 text-blue-600" />
        </div>
        <div className="absolute top-4 left-4">
          <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-medium shadow-lg">
            {course.category}
          </span>
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </div>

      {/* Course Content */}
      <div className="p-6 flex flex-col flex-1">
        {/* Course Header */}
        <div className="mb-4">
          <h3 className="text-lg font-bold text-slate-900 mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors duration-200 leading-tight min-h-[3rem]">
            {course.title}
          </h3>
          <p className="text-slate-600 text-sm line-clamp-2 mb-3 leading-relaxed min-h-[2.5rem]">
            {course.description}
          </p>
        </div>

        {/* Course Features */}
        <div className="mb-4 flex-1">
          <div className="grid grid-cols-2 gap-3 text-xs text-slate-500 mb-3">
            <div className="flex items-center space-x-2">
              <Clock className="w-4 h-4 text-blue-600" />
              <span className="font-medium">{course.duration}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Users className="w-4 h-4 text-blue-600" />
              <span className="font-medium">{course.students === 0 ? 'New' : course.students.toLocaleString()}</span>
            </div>
          </div>

          <div className="mb-3">
            <div className="flex items-center space-x-1 mb-2">
              {renderStars(course.rating)}
              <span className="text-sm text-slate-600 ml-2 font-medium">
                {course.rating} ({course.students === 0 ? 'New' : course.students.toLocaleString()})
              </span>
            </div>
            <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-full">{course.level}</span>
          </div>

          {/* Key Features - Reduced to 2 items for consistent height */}
          <div className="space-y-2">
            {course.features.slice(0, 2).map((feature, index) => (
              <div key={index} className="flex items-start space-x-2">
                <CheckCircle className="w-3 h-3 text-blue-600 mt-0.5 flex-shrink-0" />
                <span className="text-xs text-slate-600 line-clamp-1">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Course Footer */}
        <div className="mt-auto pt-4 border-t border-slate-100">
          <div className="flex items-center justify-between mb-4">
            <div className="text-2xl font-bold text-blue-600">{course.price}</div>
            <div className="text-sm text-slate-500">{course.duration}</div>
          </div>

          <Link
            to={`/purchase/${course.id}`}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg w-full group-hover:bg-blue-700 transition-all duration-300 inline-flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
          >
            <span>Purchase</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
          </Link>
        </div>
      </div>
    </motion.div>
  );
}

export default CourseCard;
